import { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ship, Camera, Video, Mic, MessageSquare, Bell, Anchor, GripVertical,
  X, ArrowLeft, Loader2, Check, Hand,
} from "lucide-react";

const SAMPLE_CHANGELOG = [
  { v: "v1.4.2", t: "Fixed transfer timeout", b: "Wire transfers no longer hang on the confirmation screen." },
  { v: "v1.4.1", t: "Faster dashboard load", b: "Cut initial render time by ~40%." },
  { v: "v1.4.0", t: "New export button", b: "Download statements as CSV from the activity page." },
];

const OPTIONS = [
  { type: "screenshot", icon: Camera, label: "Screenshot", cls: "bg-teal" },
  { type: "screen", icon: Video, label: "Record screen", cls: "bg-sunny" },
  { type: "voice", icon: Mic, label: "Voice note", cls: "bg-orange text-white" },
  { type: "text", icon: MessageSquare, label: "Write note", cls: "bg-white" },
];

const panelSpring = { type: "spring", stiffness: 280, damping: 22 };

export default function WidgetDemo() {
  const frameRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu | screenshot | screen | voice | text | whatsnew | sending | done
  const [note, setNote] = useState("");
  const [captured, setCaptured] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pos, setPos] = useState(null); // {x,y} px within frame; null = default bottom-right
  const [hintGone, setHintGone] = useState(false);
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  const reset = () => { setView("menu"); setNote(""); setCaptured(false); setRecording(false); };

  const toggle = () => {
    setHintGone(true);
    if (open) { setOpen(false); reset(); } else { setOpen(true); reset(); }
  };

  // Drag the floating button within the frame
  const onPointerDown = (e) => {
    setHintGone(true);
    const r = e.currentTarget.getBoundingClientRect();
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
    if (d.moved && frameRef.current) {
      const fr = frameRef.current.getBoundingClientRect();
      const btnW = 132, btnH = 48;
      let x = d.ox - fr.left + dx;
      let y = d.oy - fr.top + dy;
      x = Math.max(8, Math.min(fr.width - btnW, x));
      y = Math.max(8, Math.min(fr.height - btnH, y));
      setPos({ x, y });
    }
  };
  const onPointerUp = (e) => {
    drag.current.active = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };
  const onClick = () => { if (drag.current.moved) { drag.current.moved = false; return; } toggle(); };

  const simulateCapture = useCallback(() => {
    setTimeout(() => setCaptured(true), 600);
  }, []);

  const send = () => {
    setView("sending");
    setTimeout(() => setView("done"), 1100);
  };

  const btnStyle = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : { right: 16, bottom: 16 };

  return (
    <div
      ref={frameRef}
      className="relative w-full h-[520px] sm:h-[560px] nb-card p-0 overflow-hidden select-none"
      data-testid="widget-demo-frame"
    >
      {/* Fake browser bar */}
      <div className="h-11 border-b-2 border-ink flex items-center gap-1.5 px-4 bg-white">
        <div className="h-3 w-3 rounded-full border-2 border-ink bg-danger" />
        <div className="h-3 w-3 rounded-full border-2 border-ink bg-sunny" />
        <div className="h-3 w-3 rounded-full border-2 border-ink bg-grass" />
        <div className="ml-3 flex-1 bg-cream border-2 border-ink rounded-full px-3 py-0.5 text-[11px] font-mono font-600 text-ink/60">
          app.yourfintech.com/dashboard
        </div>
      </div>

      {/* Fake app content */}
      <div className="flex h-[calc(100%-2.75rem)]">
        <div className="w-16 sm:w-20 bg-sunny border-r-2 border-ink flex flex-col items-center gap-3 py-4 shrink-0">
          <div className="h-9 w-9 bg-orange border-2 border-ink rounded-xl" />
          <div className="h-7 w-7 bg-white border-2 border-ink rounded-lg" />
          <div className="h-7 w-7 bg-white border-2 border-ink rounded-lg" />
          <div className="h-7 w-7 bg-white border-2 border-ink rounded-lg" />
        </div>
        <div className="flex-1 p-5 overflow-hidden">
          <div className="h-4 w-32 bg-ink/15 rounded-full mb-4" />
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="h-20 bg-teal/40 border-2 border-ink/15 rounded-2xl" />
            <div className="h-20 bg-orange/20 border-2 border-ink/15 rounded-2xl" />
            <div className="h-20 bg-sunny/50 border-2 border-ink/15 rounded-2xl" />
          </div>
          <div className="h-28 bg-white border-2 border-ink/15 rounded-2xl mb-3" />
          <div className="h-4 w-40 bg-ink/10 rounded-full mb-2" />
          <div className="h-4 w-56 bg-ink/10 rounded-full" />
        </div>
      </div>

      {/* Hint */}
      <AnimatePresence>
        {!hintGone && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute bottom-[74px] right-5 flex items-center gap-2 bg-ink text-white text-xs font-700 px-3 py-2 rounded-full border-2 border-ink shadow-nb-sm"
          >
            <Hand className="h-4 w-4 animate-bounce" /> Click me — drag me around!
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <button
        data-testid="widget-demo-trigger"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={onClick}
        style={btnStyle}
        className="absolute z-20 flex items-center gap-2 bg-orange text-white pl-2.5 pr-4 py-3 rounded-full border-2 border-ink shadow-nb cursor-grab active:cursor-grabbing font-800 text-sm touch-none hover:-translate-y-0.5 transition-transform"
      >
        <GripVertical className="h-4 w-4 opacity-60" />
        <Ship className="h-5 w-5" />
        Feedback
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={panelSpring}
            style={{ right: 16, bottom: 76 }}
            className="absolute z-30 w-[300px] max-w-[88%] bg-cream border-2 border-ink rounded-[28px] shadow-nb-xl overflow-hidden"
            data-testid="widget-demo-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b-2 border-ink bg-sunny">
              <b className="font-display text-base font-700 flex items-center gap-1.5">
                <Anchor className="h-4 w-4" /> {headerTitle(view)}
              </b>
              <button onClick={toggle} data-testid="widget-demo-close"
                className="h-6 w-6 flex items-center justify-center bg-white border-2 border-ink rounded-md hover:bg-danger hover:text-white transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="p-4 max-h-[340px] overflow-y-auto">
              {view === "menu" && (
                <>
                  <div className="grid grid-cols-2 gap-2.5">
                    {OPTIONS.map((o) => (
                      <button key={o.type} data-testid={`widget-demo-opt-${o.type}`}
                        onClick={() => { setCaptured(false); setRecording(false); setView(o.type); }}
                        className={`flex flex-col items-start gap-2 p-3.5 border-2 border-ink rounded-2xl shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb active:translate-y-0 active:shadow-none transition-all ${o.cls}`}>
                        <o.icon className="h-5 w-5" />
                        <span className="text-xs font-800">{o.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setView("whatsnew")} data-testid="widget-demo-whatsnew"
                    className="mt-2.5 w-full flex items-center justify-center gap-2 bg-white border-2 border-ink rounded-full py-2.5 text-sm font-800 shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb transition-all">
                    <Bell className="h-4 w-4" /> What's new
                  </button>
                </>
              )}

              {view === "text" && (
                <Flow onBack={() => setView("menu")}>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
                    data-testid="widget-demo-note"
                    placeholder="Describe the issue or idea..."
                    className="w-full bg-white border-2 border-ink rounded-xl px-3 py-2.5 text-sm font-600 outline-none focus:shadow-[0_0_0_4px_#FFE66D] mb-2.5" />
                  <DemoSubject />
                  <IdentityFields />
                  <SendBtn onClick={send} />
                </Flow>
              )}

              {(view === "screenshot" || view === "screen" || view === "voice") && (
                <Flow onBack={() => setView("menu")}>
                  {!captured ? (
                    <div>
                      <div className="h-28 rounded-xl border-2 border-dashed border-ink/40 bg-white flex flex-col items-center justify-center gap-1 mb-3 text-ink/50">
                        {view === "voice"
                          ? <Mic className="h-7 w-7" />
                          : view === "screen"
                            ? <Video className="h-7 w-7" />
                            : <Camera className="h-7 w-7" />}
                        <span className="text-xs font-700">
                          {recording ? "Recording… 00:0" + Math.floor(Math.random() * 6) : "Demo capture"}
                        </span>
                      </div>
                      {view === "screenshot" ? (
                        <button data-testid="widget-demo-capture" onClick={simulateCapture}
                          className="w-full bg-orange text-white border-2 border-ink rounded-full py-3 text-sm font-800 shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb active:translate-y-0 active:shadow-none transition-all">
                          Capture screen
                        </button>
                      ) : !recording ? (
                        <button data-testid="widget-demo-capture" onClick={() => { setRecording(true); setTimeout(() => setCaptured(true), 1400); }}
                          className="w-full bg-orange text-white border-2 border-ink rounded-full py-3 text-sm font-800 shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb active:translate-y-0 active:shadow-none transition-all">
                          {view === "voice" ? "Start recording" : "Start screen recording"}
                        </button>
                      ) : (
                        <button onClick={() => setCaptured(true)}
                          className="w-full bg-danger text-white border-2 border-ink rounded-full py-3 text-sm font-800 shadow-nb-sm transition-all">
                          Stop recording
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="rounded-xl border-2 border-ink bg-white overflow-hidden mb-3">
                        {view === "voice" ? (
                          <div className="h-14 flex items-center gap-1 px-4">
                            {[...Array(22)].map((_, i) => (
                              <span key={i} className="flex-1 bg-orange rounded-full" style={{ height: `${20 + Math.abs(Math.sin(i)) * 60}%` }} />
                            ))}
                          </div>
                        ) : (
                          <div className="relative h-28 bg-gradient-to-br from-teal/40 to-sunny/40 flex items-center justify-center">
                            <div className="absolute inset-2 border-2 border-ink/15 rounded-lg bg-white/40" />
                            <div className="relative flex items-center gap-1.5 bg-ink text-white text-[11px] font-700 px-2.5 py-1 rounded-full">
                              <Check className="h-3.5 w-3.5 text-grass" /> {view === "screen" ? "Recording captured" : "Screenshot captured"}
                            </div>
                          </div>
                        )}
                      </div>
                      <DemoSubject />
                      <IdentityFields />
                      <SendBtn onClick={send} />
                    </div>
                  )}
                </Flow>
              )}

              {view === "whatsnew" && (
                <Flow onBack={() => setView("menu")}>
                  <div className="space-y-3" data-testid="widget-demo-changelog">
                    {SAMPLE_CHANGELOG.map((c) => (
                      <div key={c.v} className="border-l-4 border-orange pl-3">
                        <span className="font-mono text-[11px] text-orange font-600">{c.v}</span>
                        <h5 className="text-sm font-800">{c.t}</h5>
                        <p className="text-xs text-ink/60 font-600">{c.b}</p>
                      </div>
                    ))}
                  </div>
                </Flow>
              )}

              {view === "sending" && (
                <div className="py-8 flex flex-col items-center gap-3 text-ink/60">
                  <Loader2 className="h-6 w-6 animate-spin text-orange" />
                  <span className="text-sm font-700">Hauling it in…</span>
                </div>
              )}

              {view === "done" && (
                <div className="py-6 flex flex-col items-center gap-4 text-center" data-testid="widget-demo-done">
                  <div className="h-14 w-14 bg-grass border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm">
                    <Anchor className="h-7 w-7 text-white" />
                  </div>
                  <p className="text-sm font-800">Feedback sent. Thanks, matey! ⚓</p>
                  <button onClick={() => setView("menu")} data-testid="widget-demo-again"
                    className="bg-orange text-white border-2 border-ink rounded-full px-5 py-2.5 text-sm font-800 shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb active:translate-y-0 active:shadow-none transition-all">
                    Send another
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function headerTitle(view) {
  return {
    menu: "Ahoy! Found a bug?",
    text: "Write a note",
    screenshot: "Screenshot",
    screen: "Record screen",
    voice: "Voice note",
    whatsnew: "What's new",
    sending: "Sending",
    done: "Thank you!",
  }[view] || "Tugboat";
}

function Flow({ onBack, children }) {
  return (
    <div>
      <button onClick={onBack} data-testid="widget-demo-back"
        className="flex items-center gap-1 bg-white border-2 border-ink rounded-full px-3 py-1 text-xs font-800 mb-3 hover:-translate-y-0.5 transition-transform">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      {children}
    </div>
  );
}

function IdentityFields() {
  return (
    <>
      <p className="text-[10px] font-800 uppercase tracking-wide text-ink/50 mb-2">Your details (optional)</p>      <input placeholder="Name" data-testid="widget-demo-name"
        className="w-full bg-white border-2 border-ink rounded-xl px-3 py-2 text-sm font-600 outline-none focus:shadow-[0_0_0_4px_#FFE66D] mb-2" />
      <input placeholder="Email or contact"
        className="w-full bg-white border-2 border-ink rounded-xl px-3 py-2 text-sm font-600 outline-none focus:shadow-[0_0_0_4px_#FFE66D] mb-3" />
    </>
  );
}

function SendBtn({ onClick }) {
  return (
    <button onClick={onClick} data-testid="widget-demo-send"
      className="w-full bg-orange text-white border-2 border-ink rounded-full py-3 text-sm font-800 shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb active:translate-y-0 active:shadow-none transition-all">
      Send feedback
    </button>
  );
}

function DemoSubject() {
  return (
    <>
      <p className="text-[10px] font-800 uppercase tracking-wide text-ink/50 mb-2">Subject</p>
      <select data-testid="widget-demo-subject"
        className="w-full bg-white border-2 border-ink rounded-xl px-3 py-2 text-sm font-600 outline-none focus:shadow-[0_0_0_4px_#FFE66D] mb-3">
        <option value="">Select a subject…</option>
        {["Bug", "UI/UX", "Performance", "Feature request"].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </>
  );
}
